import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { FeedbackRibbon, FeedbackDialogParams, FeedbackCategory } from 'owa-feedback';
import sendSearchFeedback from '../helpers/sendSearchFeedback';
import sendFeedbackSignal from '../helpers/sendFeedbackSignal';
import donateSearchPayload from '../helpers/donateSearchPayload';
import { SubstrateSearchScenario } from 'owa-search-service';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { format, formatToArray } from 'owa-localize';
import {
    dialogTitle,
    feedbackCommentsPlaceholder,
    feedbackLauncherLink,
    searchFeedbackThankYouMessage,
    moreFeedback,
    noResultsOption,
    slowSearchOption,
    notRelevantOption,
    missingResultOption,
    uselessSuggestionsOption,
    hardToUseOption,
    otherOption,
    elapsedTimeMessage,
    elapsedTimeValueInMilliseconds,
    donationText,
    donationTextTooltip,
    secondaryDonationText,
    privacyPolicyLinkText,
} from './SearchInlineFeedback.locstring.json';
import { OwaDiagnosticsButtonId } from 'owa-suite-header/lib/constants';
import { updateHeaderButtonState } from 'owa-suite-header-apis';
import { Link } from 'office-ui-fabric-react/lib/components/Link';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { getSearchRequestInstrumentation } from 'owa-search-diagnostics';

const styles = require('./SearchInlineFeedback.scss');
const classNames = require('classnames');

const feedbackStrings = {
    dialogTitle: loc(dialogTitle),
    feedbackCommentsPlaceholder: loc(feedbackCommentsPlaceholder),
    feedbackLauncherLink: loc(feedbackLauncherLink),
    searchFeedbackThankYouMessage: loc(searchFeedbackThankYouMessage),
    moreFeedback: loc(moreFeedback),
    noResultsOption: loc(noResultsOption),
    slowSearchOption: loc(slowSearchOption),
    notRelevantOption: loc(notRelevantOption),
    missingResultOption: loc(missingResultOption),
    uselessSuggestionsOption: loc(uselessSuggestionsOption),
    hardToUseOption: loc(hardToUseOption),
    otherOption: loc(otherOption),
    donationText: loc(donationText),
    donationTextTooltip: loc(donationTextTooltip),
    secondaryDonationText: loc(secondaryDonationText),
    privacyPolicyLinkText: loc(privacyPolicyLinkText),
};

// User Voice teams using this component
export enum UserVoiceSearchTeam {
    MailSearch,
    CalendarSearch,
}

export interface SearchInlineFeedbackProps {
    cssClass: string;
    currentSearchQueryId: string;
    userVoiceTeam: UserVoiceSearchTeam;
}

const SearchInlineFeedback = observer(function SearchInlineFeedback(
    props: SearchInlineFeedbackProps
) {
    const feedbackCategories: FeedbackCategory[] = [
        { logString: 'No results were found', uiString: feedbackStrings.noResultsOption },
        { logString: 'Search was slow', uiString: feedbackStrings.slowSearchOption },
        {
            logString: "The results weren't relevant",
            uiString: feedbackStrings.notRelevantOption,
        },
        {
            logString: "The item I was looking for wasn't in the results",
            uiString: feedbackStrings.missingResultOption,
        },
        {
            logString: "Search suggestions weren't useful",
            uiString: feedbackStrings.uselessSuggestionsOption,
        },
        { logString: 'Search is hard to use', uiString: feedbackStrings.hardToUseOption },
        { logString: 'Other', uiString: feedbackStrings.otherOption },
    ];

    const onDonationOverrideCallback = (isContentDonated: boolean) => {
        donateSearchPayload(isContentDonated);
    };

    const feedbackDialogParams: FeedbackDialogParams = {
        title: feedbackStrings.dialogTitle,
        categories: feedbackCategories,
        ghostText: feedbackStrings.feedbackCommentsPlaceholder,
        donationText: isFeatureEnabled('sea-donateQuery')
            ? feedbackStrings.donationText
            : undefined,
        donationToolTip: isFeatureEnabled('sea-donateQuery')
            ? feedbackStrings.donationTextTooltip
            : undefined,
        donationJson: isFeatureEnabled('sea-donateQuery') ? ' ' : undefined,
        secondaryDonationText: isFeatureEnabled('sea-donateQuery')
            ? feedbackStrings.secondaryDonationText
            : undefined,
        donationOverrideCallback: isFeatureEnabled('sea-donateQuery')
            ? onDonationOverrideCallback
            : undefined,
        privacyPolicyLinkText: isFeatureEnabled('sea-donateQuery')
            ? feedbackStrings.privacyPolicyLinkText
            : undefined,
        privacyPolicyLink: isFeatureEnabled('sea-donateQuery')
            ? 'https://go.microsoft.com/fwlink/?LinkID=746667'
            : undefined,
        createUVTicket: true,
        submitCallBack: (
            inlineFeedbackResponse: string,
            inlineFeedbackResponseCategories: string[]
        ) => {
            sendSearchFeedback(
                props.userVoiceTeam,
                inlineFeedbackResponse,
                inlineFeedbackResponseCategories
            );
        },
    };

    const onYesSelected = () => {
        sendFeedbackSignal(
            props.userVoiceTeam == UserVoiceSearchTeam.MailSearch
                ? SubstrateSearchScenario.Mail
                : SubstrateSearchScenario.Calendar,
            true,
            props.currentSearchQueryId,
            feedbackStrings.feedbackLauncherLink
        );
    };

    const onNoSelected = () => {
        sendFeedbackSignal(
            props.userVoiceTeam == UserVoiceSearchTeam.MailSearch
                ? SubstrateSearchScenario.Mail
                : SubstrateSearchScenario.Calendar,
            false,
            props.currentSearchQueryId,
            feedbackStrings.feedbackLauncherLink
        );
    };

    const searchTimeInMilliseconds = getSearchTimeInMilliseconds(props.currentSearchQueryId);

    const elapsedTimeElement = format(
        loc(elapsedTimeValueInMilliseconds),
        searchTimeInMilliseconds
    );

    /**
     * Toggling diagnostics panel in this way does not work in IE. Given that
     * this is an internal, developer-focused feature and not many (if any) internal
     * developers actively use IE, let's remove the click target instead of
     * investing in a fix (because we can't even run dev builds in IE).
     *
     * Additionally, we should only enable the link when the diagnostics panel
     * is enabled, since clicking it otherwise is a no-op.
     */
    const elapsedTimeToRender =
        isFeatureEnabled('fwk-devTools') && !isBrowserIE() ? (
            <Link onClick={onElapsedTimeClick}>{elapsedTimeElement}</Link>
        ) : (
            elapsedTimeElement
        );

    return (
        <>
            {searchTimeInMilliseconds ? (
                <div className={classNames(props.cssClass, styles.inlineRibbonContainer)}>
                    <FeedbackRibbon
                        ribbonMessage={feedbackStrings.feedbackLauncherLink}
                        thankYouMessage={feedbackStrings.searchFeedbackThankYouMessage}
                        moreFeedbackRibbonMessage={feedbackStrings.moreFeedback}
                        feature="Search"
                        metadata={props.currentSearchQueryId}
                        yesCallBack={onYesSelected}
                        noCallBack={onNoSelected}
                        noFeedbackDialogParams={feedbackDialogParams}
                        yesFeedbackDialogParams={feedbackDialogParams}
                        ribbonMessageClassName={styles.searchFeedbackRibbonMessage}
                    />
                    <span className={styles.elapsedTime}>
                        {formatToArray(loc(elapsedTimeMessage), elapsedTimeToRender)}
                    </span>
                </div>
            ) : null}
        </>
    );
});

const onElapsedTimeClick = () => {
    updateHeaderButtonState(OwaDiagnosticsButtonId, true);
};

const getSearchTimeInMilliseconds = (currentSearchQueryId: string) => {
    const searchInstrumentationEntries = getSearchRequestInstrumentation().slice().reverse();

    for (const instrumentation of searchInstrumentationEntries) {
        if (
            instrumentation.PageNumber == 0 &&
            instrumentation.SearchQueryId == currentSearchQueryId
        ) {
            return instrumentation.ClientJSEndToEndTime;
        }
    }

    return 0;
};

export default SearchInlineFeedback;
